import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime, newIdempotencyKey } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { Grievance, GrievanceFiled, GrievanceState, GrievanceType } from '../../lib/types';
import { useCursorList } from '../../lib/useCursorList';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Pagination, Spinner, StatusBadge, Tabs } from '../../ui/components';

const GRIEVANCE_TYPES: GrievanceType[] = ['DISMISSAL', 'WAGES', 'CONDITIONS', 'DISCRIMINATION'];
const GRIEVANCE_STATES: GrievanceState[] = ['FILED', 'REGISTERED', 'CONCILIATION', 'SETTLED', 'CERTIFIED_TO_TRIBUNAL', 'WITHDRAWN'];

const TAB_LABEL = { file: 'File a grievance', mine: 'My grievances', caseload: 'Caseload', inbox: 'Employer inbox' } as const;
type TabKey = keyof typeof TAB_LABEL;

export function GrievancesHome() {
  const { persona } = useAuth();
  const isStaff = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_OFFICER, M16_ROLES.MEDIATOR);
  const isWorker = persona?.kind === 'worker';
  const isEmployer = persona?.kind === 'employer';

  const tabs: TabKey[] = [
    ...((isWorker || isStaff ? ['file'] : []) as TabKey[]),
    ...((isWorker ? ['mine'] : []) as TabKey[]),
    ...((isStaff ? ['caseload'] : []) as TabKey[]),
    ...((isEmployer ? ['inbox'] : []) as TabKey[]),
  ];
  const [tab, setTab] = useState<TabKey>(isStaff ? 'caseload' : isEmployer ? 'inbox' : isWorker ? 'mine' : 'file');

  return (
    <div>
      <PageHeader
        title="Grievances"
        subtitle="Employment Act 1995 worker grievances — filing through registration, conciliation, settlement, or tribunal certification."
      />
      <Tabs tabs={tabs.map((k) => ({ key: k, label: TAB_LABEL[k] }))} value={tab} onChange={setTab} />
      {tab === 'file' ? <FileTab /> : null}
      {tab === 'mine' ? <MineTab /> : null}
      {tab === 'caseload' ? <CaseloadTab /> : null}
      {tab === 'inbox' ? <InboxTab /> : null}
    </div>
  );
}

function FileTab() {
  const { persona } = useAuth();
  const isStaff = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_OFFICER, M16_ROLES.MEDIATOR);
  const [workerNin, setWorkerNin] = useState('');
  const [employerEntityId, setEmployerEntityId] = useState('');
  const [type, setType] = useState<GrievanceType | ''>('');
  const [narrative, setNarrative] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<GrievanceFiled | null>(null);

  const submit = async () => {
    if (!employerEntityId || !type || narrative.trim().length < 10) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { employerEntityId, type, narrative };
      if (isStaff && workerNin.trim()) body.workerNin = workerNin.trim();
      const res = await api.post<GrievanceFiled>('/api/v1/m16/grievances', body, { idempotencyKey: newIdempotencyKey() });
      setCreated(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Filing failed');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="card max-w-xl border-emerald-200 bg-emerald-50 p-5">
        <h2 className="text-sm font-bold text-emerald-800">Grievance filed — {created.refNo}</h2>
        <p className="mt-2 text-xs leading-relaxed text-emerald-700">
          Statutory registration deadline: <strong>{formatDateTime(created.statutoryTimeline.registrationDeadline)}</strong> (
          {created.statutoryTimeline.registrationWindowWorkingDays} working days from filing). If conciliation is needed, it runs up to{' '}
          {created.statutoryTimeline.conciliationMaxSessions} sessions within {created.statutoryTimeline.conciliationWindowDays} days.
        </p>
        <div className="mt-3 flex gap-2">
          <Link to={`/grievances/${created.id}`} className="btn-primary">
            View grievance
          </Link>
          <button
            className="btn-secondary !bg-white"
            onClick={() => {
              setCreated(null);
              setEmployerEntityId('');
              setNarrative('');
              setType('');
              setWorkerNin('');
            }}
          >
            File another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card max-w-xl p-5">
      <p className="mb-3 text-xs text-slate-500">
        {isStaff
          ? 'Officer-assisted intake for a walk-in worker, or leave the NIN blank to file under your own signed-in identity.'
          : 'Filed under your own signed-in identity — the worker NIN is taken from your session automatically.'}
      </p>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        {isStaff ? (
          <Field label="Worker NIN (officer-assisted intake)" hint="Leave blank to file as yourself instead.">
            <input className="field-input" value={workerNin} onChange={(e) => setWorkerNin(e.target.value)} placeholder="e.g. SC870512033" />
          </Field>
        ) : null}
        <Field label="Employer entity ID">
          <input className="field-input" value={employerEntityId} onChange={(e) => setEmployerEntityId(e.target.value)} placeholder="e.g. BIZ-SC-200102" />
        </Field>
        <Field label="Grievance type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as GrievanceType)}>
            <option value="">Choose a type…</option>
            {GRIEVANCE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Narrative" hint="At least 10 characters — describe what happened.">
          <textarea className="field-input min-h-[7rem]" value={narrative} onChange={(e) => setNarrative(e.target.value)} />
        </Field>
        <button className="btn-primary" onClick={submit} disabled={busy || !employerEntityId || !type || narrative.trim().length < 10}>
          {busy ? 'Filing…' : 'File grievance'}
        </button>
      </div>
    </div>
  );
}

function MineTab() {
  const { persona } = useAuth();
  const listQ = useCursorList<Grievance>(persona?.nin ? '/api/v1/m16/grievances/me' : null);
  return (
    <div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.items.length === 0 ? (
          <EmptyState title="No grievances yet" hint="Use the 'File a grievance' tab to lodge one." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Type</th>
                <th className="table-head">Employer</th>
                <th className="table-head">Filed</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.items.map((g) => (
                <tr key={g.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">
                    <Link className="font-semibold text-brand-600 hover:underline" to={`/grievances/${g.id}`}>
                      {g.refNo}
                    </Link>
                  </td>
                  <td className="table-cell text-xs">{g.type}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{g.employerEntityId}</td>
                  <td className="table-cell text-xs">{formatDate(g.filedAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={g.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination hasMore={listQ.hasMore} onMore={listQ.loadMore} loading={listQ.loadingMore} />
      </div>
    </div>
  );
}

function CaseloadTab() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const query = new URLSearchParams();
  if (status) query.set('status', status);
  if (q) query.set('q', q);
  const path = `/api/v1/m16/grievances${query.toString() ? `?${query.toString()}` : ''}`;
  const listQ = useCursorList<Grievance>(path);

  return (
    <div>
      <div className="mb-4 grid gap-2 sm:grid-cols-2">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {GRIEVANCE_STATES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Search" className="!mb-0">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ref no, employer, narrative…" />
        </Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.items.length === 0 ? (
          <EmptyState title="No grievances match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Type</th>
                <th className="table-head">Worker</th>
                <th className="table-head">Employer</th>
                <th className="table-head">Filed</th>
                <th className="table-head">SLA</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.items.map((g) => (
                <tr key={g.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">
                    <Link className="font-semibold text-brand-600 hover:underline" to={`/grievances/${g.id}`}>
                      {g.refNo}
                    </Link>
                  </td>
                  <td className="table-cell text-xs">{g.type}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{g.workerNin}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{g.employerEntityId}</td>
                  <td className="table-cell text-xs">{formatDate(g.filedAt)}</td>
                  <td className="table-cell text-xs">
                    {g.registeredAt ? (
                      <StatusBadge status={g.registeredWithinSla ? 'OK' : 'OVERDUE'} />
                    ) : (
                      <span className="text-slate-400">due {formatDate(g.registrationDeadline)}</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={g.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination hasMore={listQ.hasMore} onMore={listQ.loadMore} loading={listQ.loadingMore} />
      </div>
    </div>
  );
}

function InboxTab() {
  const { persona } = useAuth();
  const listQ = useCursorList<Grievance>('/api/v1/m16/grievances/employer', { actingFor: persona?.actingForBusinessId });

  return (
    <div>
      <InfoNote message="Requires a genuine X-Acting-For employer mandate validated against the C1 MandateRegistry. m16-labour-relations's standalone dev server does not yet wire a MandateValidator, so this call may 403 with MANDATE_INVALID/forbidden until that infrastructure lands — see the build report." />
      <div className="card mt-3 overflow-x-auto">
        {listQ.error ? (
          <div className="p-4">
            <ErrorNote message={listQ.error} />
          </div>
        ) : listQ.loading ? (
          <Spinner />
        ) : listQ.items.length === 0 ? (
          <EmptyState title="No cases" hint="Only grievances past FILED (i.e. already notified to your business) appear here." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Type</th>
                <th className="table-head">Filed</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.items.map((g) => (
                <tr key={g.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">
                    <Link className="font-semibold text-brand-600 hover:underline" to={`/grievances/${g.id}`}>
                      {g.refNo}
                    </Link>
                  </td>
                  <td className="table-cell text-xs">{g.type}</td>
                  <td className="table-cell text-xs">{formatDate(g.filedAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={g.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
